/**
 * TreeModel — 树形数据模型
 *
 * ## 架构角色
 * 树形控件（SearchTree / Tree / TreeTable）在运行时对应一个 TreeModel 实例。
 * 继承 BaseModel，负责树节点的增删改查、选中、勾选、展开等状态管理。
 *
 * ## 数据存储
 * ```
 * _data:
 *   ├── treeData     — 原始嵌套树数据数组（供渲染层直接消费）
 *   ├── columns      — treetable 栏目配置
 *   └── ...
 * cache:
 *   ├── nodeMap      — 扁平节点映射 { key: { key, parentKey, children:[keys], ...data } }
 *   ├── rootKeys     — 根节点 key 数组
 *   ├── selectedKeys — 选中节点 key 数组
 *   ├── checkedKeys  — 勾选节点 key 数组
 *   └── expandedKeys — 展开行映射 { key: true }
 * ```
 *
 * ## 事件（7 个，通过 on/execute 触发）
 * | 事件 | 说明 |
 * |------|------|
 * | beforeSetDataSource | 设置数据源前；返回 false 中止 |
 * | afterSetDataSource  | 设置数据源后 |
 * | beforeSelect        | 选中节点前；返回 false 中止 |
 * | afterSelect         | 选中节点后 |
 * | beforeCheck         | 勾选节点前；返回 false 中止 |
 * | afterCheck          | 勾选节点后 |
 * | beforeLoad          | 发起远程请求前 |
 *
 * ## 使用示例
 * ```js
 * var treeModel = new ttx.models.TreeModel({ cItemName: 'tree1', cControlType: 'tree' });
 * treeModel.setDataSource([{ key: '1', title: '根节点', children: [...] }]);
 * treeModel.on('afterSelect', function(keys) { console.log(keys); });
 * treeModel.select(['1']);
 * ```
 */
ttx.models.register('TreeModel', function (modelType) {
    const treeModel = function (data) {
        ttx.models.BaseModel.call(this, data);
        // 初始化树数据
        this._set_data('treeData', [], true);
        this._set_data('columns', [], true);
        this.setCache('nodeMap', {});
        this.setCache('rootKeys', []);
        this.setCache('selectedKeys', []);
        this.setCache('checkedKeys', []);
        this.setCache('expandedKeys', {});
        // 节点 key 自增计数器（当节点未提供 key 时使用）
        this.setCache('_keySeq', 0);
    };
    treeModel.prototype = ttx.utils.getPrototype(ttx.models.BaseModel.prototype);
    treeModel.prototype.modelType = modelType;

    // ===== 内部工具 =====

    // 获取节点 key：优先取 key，其次 id，最后自增生成
    treeModel.prototype._getNodeKey = function (node) {
        if (!node) return null;
        if (node.key != null) return node.key;
        if (node.id != null) return node.id;
        const seq = (this.getCache('_keySeq') || 0) + 1;
        this.setCache('_keySeq', seq);
        return '_auto_' + seq;
    };

    /**
     * 将嵌套树数据扁平化为 nodeMap
     * @param {Array} nodes  嵌套节点数组
     * @param {*} parentKey  父节点 key
     * @param {Object} nodeMap 扁平节点映射（输出）
     * @returns {Array} 当前层级的节点 key 数组
     */
    treeModel.prototype._flatten = function (nodes, parentKey, nodeMap) {
        const self = this;
        const keys = [];
        (nodes || []).forEach(function (node) {
            const key = self._getNodeKey(node);
            const flatNode = ttx.utils.extend({}, node, { key: key, parentKey: parentKey });
            flatNode.children = [];
            nodeMap[key] = flatNode;
            keys.push(key);
            if (node.children && node.children.length) {
                flatNode.children = self._flatten(node.children, key, nodeMap);
            }
        });
        return keys;
    };

    // 重建 nodeMap 与 rootKeys（基于 treeData）
    treeModel.prototype._rebuildNodeMap = function () {
        const treeData = this._get_data('treeData') || [];
        const nodeMap = {};
        const rootKeys = this._flatten(treeData, null, nodeMap);
        this.setCache('nodeMap', nodeMap);
        this.setCache('rootKeys', rootKeys);
    };

    // 根据嵌套数据重建 treeData（基于 nodeMap）
    treeModel.prototype._rebuildTreeData = function () {
        const nodeMap = this.getCache('nodeMap') || {};
        const rootKeys = this.getCache('rootKeys') || [];
        const self = this;

        const buildNode = function (key) {
            const flat = nodeMap[key];
            if (!flat) return null;
            const node = ttx.utils.extend({}, flat);
            delete node.parentKey;
            node.children = (flat.children || []).map(function (childKey) {
                return buildNode(childKey);
            }).filter(function (n) { return n != null; });
            return node;
        };

        const treeData = rootKeys.map(function (key) {
            return buildNode(key);
        }).filter(function (n) { return n != null; });
        this._set_data('treeData', treeData, true);
    };

    // 递归收集某节点及其所有子孙节点的 key
    treeModel.prototype._collectDescendantKeys = function (key, includeSelf) {
        const nodeMap = this.getCache('nodeMap') || {};
        const result = [];
        // walk 始终将当前 key 加入结果，再递归子节点
        const walk = function (k) {
            result.push(k);
            const node = nodeMap[k];
            if (node && node.children && node.children.length) {
                node.children.forEach(function (childKey) { walk(childKey); });
            }
        };
        if (includeSelf) {
            // 含自身：从 key 开始遍历
            walk(key);
        } else {
            // 不含自身：从直接子节点开始遍历
            const node = nodeMap[key];
            if (node && node.children && node.children.length) {
                node.children.forEach(function (childKey) { walk(childKey); });
            }
        }
        return result;
    };

    // 通知监听器树数据变化
    treeModel.prototype._notifyTreeChange = function () {
        this.doPropertyChange('setState', { treeData: this._get_data('treeData') });
    };

    // ===== 数据源 =====

    /**
     * 设置树数据源
     * - local 模式：proxyConfig 传真实数据数组
     * - 远程模式：proxyConfig 传服务地址（字符串或 { url, method }），queryParams 传请求参数，callback 回调
     * 触发 beforeSetDataSource / afterSetDataSource 事件；before 返回 false 中止
     * 支持懒加载：当 cache('lazyLoadTree') 且 cache('lazyLoadTreeByPagination') 为 true 时，按懒加载模式处理
     */
    treeModel.prototype.setDataSource = function (proxyConfig, queryParams, callback) {
        const data = { proxyConfig: proxyConfig, queryParams: queryParams, callback: callback };
        // beforeSetDataSource：返回 false 中止
        if (this.execute('beforeSetDataSource', data) === false) return;

        // 懒加载模式：后端返回数据结构需改变
        if (this.getCache('lazyLoadTree') && this.getCache('lazyLoadTreeByPagination')) {
            // 懒加载模式，后端按分页返回子节点数据，仅加载首层根节点
            this._fetchRemote(proxyConfig, queryParams, function (rows) {
                this._setTreeData(rows);
                this.execute('afterSetDataSource', data);
                if (typeof callback === 'function') callback(rows);
            }.bind(this));
            return;
        }

        if (ttx.utils.isArray(proxyConfig)) {
            // local 模式，proxyConfig 为真实数据
            this._setTreeData(proxyConfig);
            this.execute('afterSetDataSource', data);
            if (typeof callback === 'function') callback(proxyConfig);
        } else {
            // 远程模式
            this._fetchRemote(proxyConfig, queryParams, function (rows) {
                this._setTreeData(rows);
                this.execute('afterSetDataSource', data);
                if (typeof callback === 'function') callback(rows);
            }.bind(this));
        }
    };

    // 内部：设置树数据并重建映射
    treeModel.prototype._setTreeData = function (rows) {
        this._set_data('treeData', rows || [], true);
        this._rebuildNodeMap();
        this._notifyTreeChange();
    };

    // 远程数据拉取
    treeModel.prototype._fetchRemote = function (proxyConfig, queryParams, callback) {
        const self = this;
        const url = (typeof proxyConfig === 'string') ? proxyConfig : (proxyConfig && proxyConfig.url);
        if (!url) {
            if (typeof callback === 'function') callback([]);
            return;
        }
        // beforeLoad：发起请求前事件
        self.execute('beforeLoad', { url: url, queryParams: queryParams });
        ttx.ajax.xhrUtil({
            url: url,
            method: (proxyConfig && proxyConfig.method) || 'get',
            data: queryParams || {},
            success: function (res) {
                const list = (res && res.data && (res.data.list || res.data.data || res.data.treeData)) || (res && res.data) || [];
                const rows = ttx.utils.isArray(list) ? list : [];
                if (typeof callback === 'function') callback(rows);
            },
            fail: function () {
                if (typeof callback === 'function') callback([]);
            }
        });
    };

    // ===== 节点增删改查 =====

    /**
     * 添加树节点
     * @param {Object} node      节点数据
     * @param {*} parentKey      父节点 key；不传则为根节点
     */
    treeModel.prototype.addNode = function (node, parentKey) {
        if (!node) return;
        const nodeMap = this.getCache('nodeMap') || {};
        const key = this._getNodeKey(node);
        const flatNode = ttx.utils.extend({}, node, { key: key, parentKey: parentKey != null ? parentKey : null, children: [] });
        nodeMap[key] = flatNode;
        this.setCache('nodeMap', nodeMap);

        if (parentKey == null) {
            // 根节点
            const rootKeys = this.getCache('rootKeys') || [];
            rootKeys.push(key);
            this.setCache('rootKeys', rootKeys);
        } else {
            // 挂载到父节点
            const parent = nodeMap[parentKey];
            if (parent) {
                parent.children = parent.children || [];
                parent.children.push(key);
            } else {
                ttx.console.warn('[TreeModel.addNode]: 未找到父节点 key=' + parentKey);
            }
        }
        this._rebuildTreeData();
        this._notifyTreeChange();
    };

    /**
     * 删除节点（含其所有子孙节点）
     * @param {*} key 节点 key
     */
    treeModel.prototype.deleteNode = function (key) {
        const nodeMap = this.getCache('nodeMap') || {};
        const node = nodeMap[key];
        if (!node) return;
        // 收集所有子孙 key（含自身）
        const toDelete = this._collectDescendantKeys(key, true);
        const parentKey = node.parentKey;

        // 从父节点 children 中移除
        if (parentKey != null && nodeMap[parentKey]) {
            const parent = nodeMap[parentKey];
            parent.children = (parent.children || []).filter(function (k) { return k !== key; });
        } else {
            // 根节点
            const rootKeys = (this.getCache('rootKeys') || []).filter(function (k) { return k !== key; });
            this.setCache('rootKeys', rootKeys);
        }
        // 删除 nodeMap 中所有相关节点
        toDelete.forEach(function (k) { delete nodeMap[k]; });
        this.setCache('nodeMap', nodeMap);
        // 清理选中/勾选中已删除的 key
        this.setCache('selectedKeys', (this.getCache('selectedKeys') || []).filter(function (k) { return toDelete.indexOf(k) < 0; }));
        this.setCache('checkedKeys', (this.getCache('checkedKeys') || []).filter(function (k) { return toDelete.indexOf(k) < 0; }));

        this._rebuildTreeData();
        this._notifyTreeChange();
    };

    /**
     * 更新节点数据
     * @param {Object} node 节点数据（需包含 key）
     */
    treeModel.prototype.updateNode = function (node) {
        if (!node) return;
        const key = this._getNodeKey(node);
        const nodeMap = this.getCache('nodeMap') || {};
        const existing = nodeMap[key];
        if (!existing) {
            ttx.console.warn('[TreeModel.updateNode]: 未找到节点 key=' + key);
            return;
        }
        // 合并更新，保留 parentKey 与 children 结构
        const parentKey = existing.parentKey;
        const children = existing.children;
        nodeMap[key] = ttx.utils.extend({}, existing, node, { key: key, parentKey: parentKey, children: children });
        this.setCache('nodeMap', nodeMap);
        this._rebuildTreeData();
        this._notifyTreeChange();
    };

    // ===== 选中 =====

    /**
     * 设置选中的树节点
     * @param {Array|*} selectedKeys 选中节点的 key 数组（单个 key 也可）
     * 触发 beforeSelect / afterSelect 事件；before 返回 false 中止
     */
    treeModel.prototype.select = function (selectedKeys) {
        const keys = ttx.utils.isArray(selectedKeys) ? selectedKeys : (selectedKeys != null ? [selectedKeys] : []);
        const data = { selectedKeys: keys };
        // beforeSelect：返回 false 中止
        if (this.execute('beforeSelect', data) === false) return;
        this.setCache('selectedKeys', keys);
        this.execute('afterSelect', data);
        // 通知监听器
        this.doPropertyChange('setState', { selectedKeys: keys });
    };

    /**
     * 获取已选中的树节点
     * @returns {Array} 节点数据数组
     */
    treeModel.prototype.getSelectedNodes = function () {
        const nodeMap = this.getCache('nodeMap') || {};
        const keys = this.getCache('selectedKeys') || [];
        return keys.map(function (k) { return nodeMap[k]; }).filter(function (n) { return n != null; });
    };

    /**
     * 获取已选中树节点的 key 值
     * @returns {Array} key 数组
     */
    treeModel.prototype.getSelectedKeys = function () {
        return this.getCache('selectedKeys') || [];
    };

    // ===== 勾选 =====

    /**
     * 设置勾选树节点（需树组件 checkable 为 true）
     * @param {Array|*} checkedKeys 勾选节点的 key 数组（单个 key 也可）
     * 触发 beforeCheck / afterCheck 事件；before 返回 false 中止
     */
    treeModel.prototype.check = function (checkedKeys) {
        const keys = ttx.utils.isArray(checkedKeys) ? checkedKeys : (checkedKeys != null ? [checkedKeys] : []);
        const data = { checkedKeys: keys };
        // beforeCheck：返回 false 中止
        if (this.execute('beforeCheck', data) === false) return;
        this.setCache('checkedKeys', keys);
        this.execute('afterCheck', data);
        // 通知监听器
        this.doPropertyChange('setState', { checkedKeys: keys });
    };

    /**
     * 获取已勾选的树节点
     * @returns {Array} 节点数据数组
     */
    treeModel.prototype.getCheckedNodes = function () {
        const nodeMap = this.getCache('nodeMap') || {};
        const keys = this.getCache('checkedKeys') || [];
        return keys.map(function (k) { return nodeMap[k]; }).filter(function (n) { return n != null; });
    };

    /**
     * 获取已勾选树节点的 key 值
     * @returns {Array} key 数组
     */
    treeModel.prototype.getCheckedKeys = function () {
        return this.getCache('checkedKeys') || [];
    };

    // ===== 展开 / 栏目 =====

    /**
     * 展开/收起树表某行数据
     * @param {number|*} indexOrRowKey 行索引或行 key
     */
    treeModel.prototype.expandRow = function (indexOrRowKey) {
        const expandedKeys = this.getCache('expandedKeys') || {};
        // 数字索引时转换为 key
        let key = indexOrRowKey;
        if (typeof indexOrRowKey === 'number') {
            const rootKeys = this.getCache('rootKeys') || [];
            key = rootKeys[indexOrRowKey];
            if (key == null) {
                // 尝试从 nodeMap 中按顺序取
                const nodeMap = this.getCache('nodeMap') || {};
                const allKeys = Object.keys(nodeMap);
                key = allKeys[indexOrRowKey];
            }
        }
        if (key == null) return;
        // 切换展开/收起状态
        if (expandedKeys[key]) {
            delete expandedKeys[key];
        } else {
            expandedKeys[key] = true;
        }
        this.setCache('expandedKeys', expandedKeys);
        this.execute('expandChange', { key: key, expanded: !!expandedKeys[key] });
        // 通知监听器
        this.doPropertyChange('setState', { expandedKeys: expandedKeys });
    };

    /**
     * 设置 treetable 栏目
     * @param {Array} columns   栏目配置数组
     * @param {Array} fieldNames 需要保留的字段名数组（可选，按 field 过滤）
     */
    treeModel.prototype.setColumns = function (columns, fieldNames) {
        const data = { columns: columns, fieldNames: fieldNames };
        if (this.execute('beforeSetColumns', data) === false) return;
        let cols = columns || [];
        if (fieldNames && ttx.utils.isArray(fieldNames)) {
            cols = cols.filter(function (c) {
                return fieldNames.indexOf(c.field || c.cItemName) >= 0;
            });
        }
        this._set_data('columns', cols, true);
        this.execute('afterSetColumns', data);
        // 通知监听器
        this.doPropertyChange('setState', { columns: cols });
    };

    return treeModel;
});
